import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { usePageMeta, type StructuredData } from "@/shared/seo/usePageMeta";

export type PublicInfoNavItem = {
  href: string;
  label: string;
};

type PublicInfoPageProps = {
  children?: ReactNode;
  canonicalPath: string;
  description: string;
  documentTitle: string;
  headingId: string;
  htmlContent?: string;
  navItems: PublicInfoNavItem[];
  pageMeta: string;
  structuredData?: StructuredData;
  title: string;
};

export function PublicInfoPage({
  children,
  canonicalPath,
  description,
  documentTitle,
  headingId,
  htmlContent,
  navItems,
  pageMeta,
  structuredData,
  title,
}: PublicInfoPageProps) {
  usePageMeta({
    canonicalPath,
    description,
    structuredData,
    title: documentTitle,
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [canonicalPath]);

  return (
    <div className="public-info-page">
      <div className="public-info-silk" aria-hidden="true">
        <div className="public-info-silk-layer public-info-silk-base" />
        <div className="public-info-silk-layer public-info-silk-drape" />
        <div className="public-info-silk-layer public-info-silk-ridges" />
        <div className="public-info-silk-layer public-info-silk-valleys" />
        <div className="public-info-silk-layer public-info-silk-sheen" />
        <div className="public-info-silk-grain" />
      </div>
      <div className="public-info-veil" aria-hidden="true" />

      <header className="public-info-header">
        <a className="public-info-brand" href="/" aria-label="Liminl home">
          liminl
        </a>
        <Link className="public-info-login" to="/login">
          Log in
        </Link>
      </header>

      <main className="public-info-main">
        <div className="public-info-layout">
          <aside className="public-info-sidebar">
            <p className="public-info-kicker">On this page</p>
            <nav aria-label={`${title} sections`}>
              {navItems.map((item) => (
                <a href={item.href} key={item.href}>
                  {item.label}
                </a>
              ))}
            </nav>
            <p className="public-info-meta">{pageMeta}</p>
          </aside>

          <article className="public-info-article" aria-labelledby={headingId}>
            <h1 id={headingId}>{title}</h1>
            {htmlContent ? (
              <div
                className="public-info-content"
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            ) : (
              <div className="public-info-content">{children}</div>
            )}
          </article>
        </div>
      </main>

      <footer className="public-info-footer">
        <p>&copy; Liminl 2026</p>
        <nav aria-label="Legal and contact">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="mailto:nicole@goliminl.com">Contact</a>
        </nav>
      </footer>
    </div>
  );
}
